import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { openAsk } from 'shared/askDraft';
import { createAgentFromTemplate } from 'features/agents/api/agentsApi';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';

/**
 * Label: the dataset's way into labeling and processing its rows. Every
 * choice makes an agent (docs/DATASETS.md): Lucid builds one from what the
 * person describes, or a template opens as a draft already pointed at this
 * dataset. Nothing runs until the person runs the agent.
 */
const TEMPLATES = [
  { id: 'label_expected_by_agreement', label: 'Where models agree' },
  { id: 'label_expected_with_model', label: 'With a model and your rules' },
  { id: 'relabel_failed_rows', label: 'Rewrite failed rows' },
  { id: 'compare_models_on_dataset', label: 'Compare models' },
];

export function lucidDraft(name) {
  return `Build an agent that labels Expected on the "${name}" dataset. `
    + 'Rows: the ones without Expected. Models: . Rule for Expected: ';
}

const ITEM = 'flex w-full items-center px-2.5 py-1.5 text-left text-[12.5px] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] disabled:opacity-50';

export default function LabelMenu({ datasetId, datasetName, neutral = false }) {
  const { team } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      if (!t) return;
      setPos({ top: t.bottom + 4, left: Math.max(8, Math.min(window.innerWidth - 230, t.right - 210)) });
    };
    place();
    const onDoc = (e) => { if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return; setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', place, true); window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const withLucid = () => { setOpen(false); openAsk(lucidDraft(datasetName || 'this')); };

  const fromTemplate = async (id) => {
    setBusy(id); setError(null);
    try {
      const out = await createAgentFromTemplate(id, { dataset_id: datasetId });
      setOpen(false);
      navigate(`/${encodeURIComponent(team || '')}/agents/${out.workflow.id}`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={neutral
          ? activityToolbarButtonClass
          : 'inline-flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-ink-onBrand hover:opacity-90'}
      >
        Label
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" aria-label="Label rows" className="fixed z-[10000] w-[210px] overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] py-1 shadow-lg" style={{ top: pos.top, left: pos.left }}>
          <button type="button" role="menuitem" onClick={withLucid} className={`${ITEM} font-medium`}>Label with Lucid</button>
          <div className="my-1 border-t border-[color:var(--border-default)]" />
          <div className="px-2.5 pb-0.5 pt-1 text-[11px] text-[color:var(--text-muted)]">From a template</div>
          {TEMPLATES.map((t) => (
            <button key={t.id} type="button" role="menuitem" disabled={!!busy} onClick={() => fromTemplate(t.id)} className={`${ITEM} justify-between`}>
              {t.label}
              {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--text-muted)]" /> : null}
            </button>
          ))}
          {error ? <div role="alert" className="px-2.5 py-1.5 text-[11.5px] text-danger">{error}</div> : null}
        </div>,
        document.body,
      )}
    </>
  );
}
